import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Input, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import UseApi from "../../../Reusable/CallApi/UseApi";
// import UseApi from "./api/UseApi";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f4f5f7;
  padding: 32px;
  font-family: "Poppins", "Inter", sans-serif;
`;

const ContentCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #edeff2;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.04);
  padding: 24px 24px 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: #1e2a3a;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #8492a6;
  margin: 6px 0 0;
`;

const SearchInputWrapper = styled.div`
  .ant-input-affix-wrapper {
    width: 260px;
    border-radius: 24px;
    border: 1px solid #e4e7eb;
    padding: 8px 16px;
  }

  .ant-input {
    font-size: 14px;
    color: #1e2a3a;
  }

  .anticon {
    color: #8492a6;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: #f7f8fa;
    color: #1e2a3a;
    font-size: 14px;
    font-weight: 600;
    border-bottom: none;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    font-size: 14px;
    font-weight: 400;
    color: #5a6b85;
    border-bottom: 1px solid #f0f1f3;
    cursor: pointer;
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: #fafbfc;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f4f5f7 !important;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 4px;
  padding: 24px 0;
`;

const PagerButton = styled.button`
  width: 36px;
  height: 32px;
  border: none;
  background: #d9dce1;
  color: #ffffff;
  font-size: 16px;
  cursor: pointer;

  &:first-child {
    border-radius: 16px 0 0 16px;
  }

  &:last-child {
    border-radius: 0 16px 16px 0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const columns = [
  {
    title: "Name",
    dataIndex: "groupName",
    key: "groupName",
  },
  {
    title: "No. of People in",
    dataIndex: "peopleCount",
    key: "peopleCount",
  },
];

const PAGE_SIZE = 4;

const GroupReportPage = () => {
  const navigate = useNavigate();
  const clientId = useSelector(
    (state) => state.authTracking.userInfo.clientId
  );
  const { request, loading } = UseApi();

  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!clientId) return;

    request({
      endPoint: `/report/getgroupreport/${clientId}`,
      method: "get",
    })
      .then((result) => setGroups(result?.response || []))
      .catch(() => setGroups([]));
  }, [clientId, request]);

  const filteredData = groups.filter((group) =>
    group.groupName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE) || 1;
  const pagedData = filteredData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleRowClick = (record) => ({
    onClick: () =>
      navigate(`/group-report/${record.groupId}`, { state: { group: record } }),
  });

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  return (
    <PageWrapper>
      <ContentCard>
        <HeaderRow>
          <div>
            <Title>Group Report</Title>
            <Subtitle>Total Group: {filteredData.length}</Subtitle>
          </div>
          <SearchInputWrapper>
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </SearchInputWrapper>
        </HeaderRow>

        <StyledTable
          columns={columns}
          dataSource={pagedData}
          rowKey="groupId"
          loading={loading}
          pagination={false}
          onRow={handleRowClick}
        />

        <PaginationWrapper>
          <PagerButton
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
          >
            &#8249;
          </PagerButton>
          <PagerButton
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((page) => Math.min(page + 1, totalPages))
            }
          >
            &#8250;
          </PagerButton>
        </PaginationWrapper>
      </ContentCard>
    </PageWrapper>
  );
};

export default GroupReportPage;